import {
  EXEC_DERIVED_COLUMNS,
  EXEC_DERIVED_COLUMNS_0085,
  EXEC_DERIVED_REGISTER_COLUMNS,
  EXEC_DERIVED_TABLE,
} from "./execDerived.mjs";
import type { ExecDerivedRegisterRow, ExecDerivedRow } from "./execDerived.d.mts";
import { ExecKpiFetchError, fetchAllPages, isTableMissingMessage } from "./execKpiFetch.mjs";
import { isColumnMissingError } from "./sanityGate";
import { getSupabase } from "./supabase";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * The Executive page's read of public.exec_derived (migration 0085), the
 * Meridian-derived board: built from a no-workbook tenant's imports, or over
 * a workbook tenant's own lines (AJ 2026-09-30).
 *
 * Client readable (tenant policy, like exec_kpi and exec_line_source), bound
 * to the tenant through scopeTenant (MER-245), and paged past PostgREST's
 * 1,000-row cap with the same fetchAllPages the workbook read uses.
 *
 * Until 0085 is applied the table does not exist: the read resolves to no
 * rows with `tableMissing`, and the page shows its awaiting card. Until 0093
 * is applied the line_state and awaiting_source columns do not exist: the
 * read falls back to 0085's columns, and the page shows the plain awaiting
 * label, exactly as before. Any other failure throws ExecKpiFetchError, and
 * the page shows fixed copy.
 */

export interface ExecDerivedFetchResult {
  rows: ExecDerivedRow[];
  truncated: boolean;
  tableMissing: boolean;
}

export interface ExecDerivedRegisterFetchResult {
  rows: ExecDerivedRegisterRow[];
  truncated: boolean;
  tableMissing: boolean;
}

function isMissing(message: string): boolean {
  return isTableMissingMessage(message) || isColumnMissingError(message);
}

async function readAll<T>(tenantId: string, columns: string) {
  const supabase = getSupabase();
  return fetchAllPages<T>(
    (from, to) =>
      scopeTenant(supabase.from(EXEC_DERIVED_TABLE).select(columns), tenantId)
        .order("sheet", { ascending: true })
        .order("line_item", { ascending: true })
        .order("period", { ascending: true })
        .range(from, to)
        // The select list is a runtime string (pinned against 0085 and 0093
        // by exec-derived.test.mjs), so the row type is asserted here.
        .returns<T[]>(),
    { isMissing },
  );
}

function missing(err: unknown): boolean {
  return err instanceof ExecKpiFetchError && err.tableMissing;
}

export async function fetchExecDerivedRows(tenantId: string): Promise<ExecDerivedFetchResult> {
  try {
    const result = await readAll<ExecDerivedRow>(tenantId, EXEC_DERIVED_COLUMNS);
    return { rows: result.rows, truncated: result.truncated, tableMissing: false };
  } catch (err) {
    if (!missing(err)) throw err;
  }
  // 0093 not applied yet (or the table itself missing): 0085's columns.
  try {
    const result = await readAll<ExecDerivedRow>(tenantId, EXEC_DERIVED_COLUMNS_0085);
    return { rows: result.rows, truncated: result.truncated, tableMissing: false };
  } catch (err) {
    if (missing(err)) return { rows: [], truncated: false, tableMissing: true };
    throw err;
  }
}

/**
 * The staff variance register's read: the derived page's figure, state and
 * reasons per line and month. `tableMissing` when the table or 0093's
 * columns are not there yet; the register then describes the workbook page's
 * source switch instead.
 */
export async function fetchExecDerivedRegisterRows(
  tenantId: string,
): Promise<ExecDerivedRegisterFetchResult> {
  try {
    const result = await readAll<ExecDerivedRegisterRow>(tenantId, EXEC_DERIVED_REGISTER_COLUMNS);
    return { rows: result.rows, truncated: result.truncated, tableMissing: false };
  } catch (err) {
    if (missing(err)) return { rows: [], truncated: false, tableMissing: true };
    throw err;
  }
}
